import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';

const calculateManualCost = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];

  const { manualCost } = improvementsForTransaction ?? {};

  if (isUndefined(manualCost)) {
    return undefined;
  }

  return {
    totalCost: AloftPrice.schema.parse(manualCost),
  };
};

const getTotalManualCost = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  id: string,
) => {
  const costOutput = calculateManualCost(improvements, id);

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return costOutput.totalCost;
};

export const selectManualCost = createPriceSelector(
  [selectImprovementsEntities, (_state, id: string) => id],
  getTotalManualCost,
);

export const selectManualCosts = createSelector(
  [selectImprovementsEntities, selectCompSequence],
  (improvements, compSequence) => {
    const properties = ['subject', ...compSequence];

    return properties.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalManualCost(improvements, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {});
  },
);

export const selectManualCostDisplay = createSelector([selectManualCost], (manualCost) => {
  if (isUndefined(manualCost)) {
    return undefined;
  }

  return manualCost.toString(true);
});
